import React, { useEffect, useState } from 'react';
import { api } from '../../../utils/api';
import { toast } from 'sonner';
import { Wrench, X, Plus } from 'lucide-react';
import { Button } from '../../../components/ui/button';

/**
 * Project ↔ Machines linker. Visar och lägger till maskiner kopplade till projektet.
 * Maskiner kan vara kopplade till flera projekt samtidigt (många-till-många).
 */
const ProjectMachinesCard = ({ project, onUpdated }) => {
  const [allMachines, setAllMachines] = useState([]);
  const [selectedIds, setSelectedIds] = useState(project.machine_ids || []);
  const [picker, setPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSelectedIds(project.machine_ids || []); }, [project.machine_ids]);

  useEffect(() => {
    api.getMachines().then(res => setAllMachines(res.data || [])).catch(() => {});
  }, []);

  const linkedMachines = allMachines.filter(m => selectedIds.includes(m.id));
  const unlinkedMachines = allMachines.filter(m => !selectedIds.includes(m.id));

  const save = async (newIds) => {
    setSaving(true);
    try {
      await api.setProjectMachines(project.id, newIds);
      setSelectedIds(newIds);
      toast.success('Maskiner uppdaterade');
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara');
    } finally { setSaving(false); }
  };

  const addMachine = (id) => save([...selectedIds, id]);
  const removeMachine = (id) => save(selectedIds.filter(x => x !== id));

  return (
    <div className="bg-card border border-border rounded-xl p-5 mb-4" data-testid="project-machines-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2">
            <Wrench size={14} className="text-[#E8613A]" /> Anlitade maskiner
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {linkedMachines.length} maskiner kopplade till projektet
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setPicker(true)} disabled={saving || unlinkedMachines.length === 0}
          data-testid="add-project-machine-btn">
          <Plus size={12} className="mr-1" /> Anlita maskin
        </Button>
      </div>
      {linkedMachines.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">Inga maskiner anlitade än. Klicka på "Anlita maskin" för att koppla en.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {linkedMachines.map((m) => (
            <div key={m.id} className="inline-flex items-center gap-2 bg-[#E8613A]/10 text-[#E8613A] border border-[#E8613A]/30 rounded-full px-3 py-1.5"
              data-testid={`linked-machine-${m.id}`}>
              <Wrench size={12} />
              <span className="text-xs font-semibold">{m.name}</span>
              {m.registration && <span className="text-[10px] text-muted-foreground">({m.registration})</span>}
              <button onClick={() => removeMachine(m.id)} className="hover:bg-[#E8613A]/20 rounded-full p-0.5" data-testid={`unlink-machine-${m.id}`}>
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {picker && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" onClick={() => setPicker(false)}>
          <div className="bg-card rounded-xl border border-border max-w-md w-full max-h-[70vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold">Välj maskin att anlita</h4>
              <button onClick={() => setPicker(false)} className="p-1 hover:bg-muted rounded">
                <X size={14} />
              </button>
            </div>
            {unlinkedMachines.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">Alla maskiner är redan anlitade på detta projekt.</p>
            ) : (
              <div className="space-y-1">
                {unlinkedMachines.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => { addMachine(m.id); setPicker(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted border border-border text-left"
                    data-testid={`pick-machine-${m.id}`}
                  >
                    <Wrench size={14} className="text-[#E8613A] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{m.name}</p>
                      {(m.registration || m.type) && (
                        <p className="text-[10px] text-muted-foreground truncate">
                          {[m.type, m.registration].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectMachinesCard;
